import React, { useEffect, useState } from "react"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Fade,
  styled,
  InputAdornment,
} from "@mui/material"
import * as Yup from "yup"

interface DynamicModalProps {
  open: boolean
  onClose: () => void
  title: string
  fieldLabel: string
  fieldPlaceholder: string
  primaryBtnText: string
  secondaryBtnText: string
  handleOnClick: (
    value: string,
    fieldKey: "firstName" | "lastName" | "role" | "email" | "mobile"
  ) => void
  fieldKey: "firstName" | "lastName" | "role" | "email" | "mobile"
  isMobileField?: boolean
  defaultValue: string
  errorMessage?: string
}

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[5],
  padding: theme.spacing(5),
  borderRadius: "20px",
  width: "500px",
  outline: "none",
  "@media (min-width: 320px) and (max-width: 510px)": {
    width: "auto",
  },
  "& .titleText": {
    color: "#000000",
    fontWeight: 500,
    fontSize: "20px",
    paddingBottom: "20px",
    marginBottom: "20px",
    lineHeight: "24.38px",
    borderBottom: "1px solid #DFDFDF",
  },
  "& .emailText": {
    color: "#010101",
    fontWeight: 500,
    fontSize: "15px",
    marginTop: "15px",
    lineHeight: "25px",
  },
  "& .fieldLabelText": {
    color: "#0F172A",
    fontWeight: 400,
    fontSize: "14px",
    margin: "15px 0px 4px",
    lineHeight: "22px",
  },
  "& .input": {
    lineHeight: "24px",
    fontSize: "16px",
    fontWeight: "400",
    color: "#64748b !important",
    "& .MuiOutlinedInput-root": {
      borderRadius: "20px",
    },
    "@media (max-width: 414px)": {
      fontSize: "12px",
      lineHeight: "20px",
    },
    "@media (min-width: 415px) and (max-width: 1280px)": {
      fontSize: "14px",
      lineHeight: "22px",
    },
    "@media (min-width: 1600px)": {
      fontSize: "18px",
      lineHeight: "26px",
    },
  },
  "& input::placeholder": {
    lineHeight: "24px",
    opacity: "1 !important",
    fontSize: "16px",
    color: "#64748b !important",
    "& .MuiOutlinedInput-root": {
      borderRadius: "20px",
    },
  },
  "& .errorMessage": {
    color: "#E11B23",
    fontSize: "12px",
    fontWeight: 500,
    marginTop: 6,
  },
  "& .countryCode": {
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "24px",
  },
  "& .btnContainer": {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
    gap: "15px",
  },
  "& .saveBtn": {
    fontSize: "14px",
    fontWeight: "600",
    width: "140px !important",
    height: "56px",
    color: "#fff !important",
    textTransform: "none",
    borderRadius: "32px",
    background: "linear-gradient(90deg, #8077FF 0%, #652784 60%)",
    opacity: 1,
    transition: "opacity 0.3s",
    "&:disabled": {
      opacity: 0.5,
      pointerEvents: "none",
    },
  },
  "& .cancelBtn": {
    fontSize: "14px",
    fontWeight: "600",
    width: "140px !important",
    height: "56px",
    color: "#652784 !important",
    textTransform: "none",
    borderRadius: "32px",
    borderColor: "#652784",
  },
}))

const currentInfo = {
  email: "Your current mail address is: ",
  mobile: "Your current mobile number is: ",
}

const inputModeMap: { [key: string]: "numeric" | "email" } = {
  mobile: "numeric",
  email: "email",
};

const UserDetailsModal: React.FC<DynamicModalProps> = ({
  open,
  onClose,
  title,
  fieldLabel,
  fieldPlaceholder,
  primaryBtnText,
  secondaryBtnText,
  handleOnClick,
  defaultValue,
  fieldKey,
  isMobileField = false,
  errorMessage,
}) => {
  const currentInfoList = ["email", "mobile"]
  const [inputValue, setInputValue] = useState("")
  const [error, setError] = useState("")
  const [isFormValid, setIsFormValid] = useState(false)
  const isMobileNumField = fieldKey === "mobile"

  useEffect(() => {
    if (errorMessage) {
      setError(errorMessage)
    }
  }, [errorMessage, open])

  useEffect(() => {
    setInputValue("")
    setError("")
    setIsFormValid(false)
  }, [open])
  
  const getInputMode = (type: string) => {
    return inputModeMap[type] || "text";
  };

  const validateInput = (value: string) => {
    let schema
    if (isMobileField) {
      schema = Yup.string()
        .required("Phone number is required!")
        .transform((value) => value.replace(/[^0-9]/g, ""))
        .matches(/^\d{10}$/, "Phone number must contain 10 digits!")
    } else if (!isMobileField && fieldLabel.toLowerCase().includes("email")) {
      schema = Yup.string()
        .transform((value) => value?.trim())
        .email("Invalid email format!")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid email format!"
        )
        .required("Email is required!")
    } else {
      schema = Yup.string()
        .required("Field is required!")
        .max(30, "Field must be at most 30 characters!")
        .matches(/^[A-Za-z\s]+$/, "Only alphabets and spaces are allowed!")
    }
    try {
      schema.validateSync(value)
      setError("")
      return true
    } catch (err: any) {
      setError(err.message)
      return false
    }
  }

  const handleSave = () => {
    if (isFormValid && fieldKey) {
      handleOnClick(inputValue, fieldKey)
    }
  }

  const handleClose = () => {
    setError("")
    onClose()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    if (isMobileField) {
      value = value.replace(/[^0-9]/g, "")
    }
    setInputValue(value)
    const isValid = validateInput(value)
    setIsFormValid(isValid)
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Fade in={open}>
        <ModalBox>
          <Typography className="titleText">{title}</Typography>

          {currentInfoList.includes(fieldKey || "") && (
            <Typography className="emailText">
              {currentInfo[fieldKey as keyof typeof currentInfo]}
              <strong style={{ fontWeight: 500 }}>
                {fieldKey === "email" ? defaultValue : `+91 ${defaultValue}`}
              </strong>
            </Typography>
          )}
          <Typography className="fieldLabelText">{fieldLabel}</Typography>

          <TextField
            className="input"
            fullWidth
            autoFocus
            variant="outlined"
            autoComplete="off"
            placeholder={fieldPlaceholder}
            value={inputValue}
            type={isMobileNumField ? 'tel' : 'text'}
            inputMode={getInputMode(fieldKey)}
            onChange={handleChange}
            inputProps={{
              ...(isMobileField
                ? { maxLength: 10 }
                : fieldLabel.toLowerCase().includes("email")
                ? { maxLength: 50 }
                : { maxLength: 30 }),
            }}
            InputProps={
              isMobileField
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography className="countryCode">+91</Typography>
                      </InputAdornment>
                    ),
                  }
                : {}
            }
          />
          {error && (
            <Typography className="errorMessage">
              {error}
            </Typography>
          )}
          <Box className="btnContainer">
            <Button
              className="saveBtn"
              variant="contained"
              onClick={handleSave}
              disabled={!isFormValid}
            >
              {primaryBtnText}
            </Button>

            <Button
              className="cancelBtn"
              variant="outlined"
              onClick={handleClose}
            >
              {secondaryBtnText}
            </Button>
          </Box>
        </ModalBox>
      </Fade>
    </Modal>
  )
}

export default UserDetailsModal
